import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser, faPhone, faIdBadge, faStar, faBoxOpen, faLink,
  faCreditCard, faBrain, faHistory, faChevronLeft, faSpinner,
  faCheckCircle, faTimesCircle, faExclamationTriangle,
  faBan, faUnlock, faArrowTrendUp, faCircle, faBell,
  faEye, faHeart, faHandPointRight, faShieldHalved, faXmark,
} from "@fortawesome/free-solid-svg-icons";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN");

const PACK_COLORS = {
  free: "bg-slate-100 text-slate-600",
  single_unlock: "bg-sky-100 text-sky-700",
  spark_bundle: "bg-indigo-100 text-indigo-700",
  premium: "bg-violet-100 text-violet-700",
  elite: "bg-amber-100 text-amber-700",
  annual: "bg-emerald-100 text-emerald-700",
};

const STATUS_COLORS = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  BLOCKED: "bg-red-100 text-red-700",
  INACTIVE: "bg-slate-100 text-slate-600",
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const QuotaBar = ({ label, used, limit, color = "#6366F1" }) => {
  const pct = limit > 0 ? Math.min(Math.round((used / limit) * 100), 100) : 0;
  const isExhausted = limit > 0 && used >= limit;
  return (
    <div>
      <div className="flex justify-between items-center mb-1">
        <span className="text-xs text-slate-500">{label}</span>
        <span className={`text-xs font-semibold ${isExhausted ? "text-red-500" : "text-slate-700"}`}>
          {fmt(used)} / {limit === -1 ? "∞" : fmt(limit)}
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${limit === -1 ? 30 : pct}%`, background: isExhausted ? "#EF4444" : color }}
        />
      </div>
    </div>
  );
};

const SectionTitle = ({ icon, title, sub }) => (
  <div className="flex items-center gap-2 mb-4">
    <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
      <FontAwesomeIcon icon={icon} className="text-indigo-600 text-sm" />
    </div>
    <div>
      <p className="font-semibold text-slate-800 text-sm">{title}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  </div>
);

const EVENT_ICONS = {
  view_start: faEye,
  view_end: faEye,
  profile_save: faHeart,
  pass: faTimesCircle,
  connection_sent: faLink,
  score_unlock: faUnlock,
  alignment_tab_open: faBrain,
  astrology_tab_open: faStar,
  photo_swipe: faArrowTrendUp,
};

const EVENT_LABELS = {
  view_start: "Profile Views",
  view_end: "View Sessions",
  photo_swipe: "Photo Swipes",
  photo_zoom: "Photo Zooms",
  alignment_tab_open: "Alignment Tab Opens",
  astrology_tab_open: "Astrology Tab Opens",
  hobbies_read: "Hobbies Read",
  profile_save: "Profiles Saved",
  pass: "Profiles Passed",
  connection_sent: "Connects Sent",
  alignment_unlock: "Alignment Unlocks",
  score_unlock: "Score Unlocks",
  score_view: "Score Views",
  return_visit: "Return Visits",
  report: "Reports Filed",
  block: "Profiles Blocked",
};

const UserDeepDive = () => {
  const { seekerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [notifyModal, setNotifyModal] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ title: '', body: '' });
  const [notifying, setNotifying] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await func.get(`mst-admin/match-seekers/${seekerId}/overview`);
        if (res?.status === 200 && res.data?.success) {
          setData(res.data);
        } else {
          toast.error("Failed to load user overview");
        }
      } catch {
        toast.error("Failed to load user overview");
      }
      setLoading(false);
    };
    load();
  }, [seekerId]);

  const handleQuickAction = async (action) => {
    if (!window.confirm(`${action === 'block' ? 'Block' : 'Unblock'} this user?`)) return;
    setActing(true);
    try {
      const res = await func.post(`mst-admin/match-seekers/${seekerId}/quick-action`, { action });
      if (res?.status === 200) {
        toast.success(`User ${action === 'block' ? 'blocked' : 'unblocked'} successfully`);
        setData(prev => ({
          ...prev,
          user: { ...prev.user, accountStatus: res.data.newStatus },
        }));
      } else {
        toast.error("Action failed");
      }
    } catch {
      toast.error("Action failed");
    }
    setActing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-indigo-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <p className="text-slate-500">User not found.</p>
        <button onClick={() => navigate(-1)} className="text-indigo-600 text-sm underline">Go back</button>
      </div>
    );
  }

  const { seeker, user, pack, profiles, connections, payments, alignment, activity, adminHistory } = data;
  const isBlocked = user?.accountStatus === "BLOCKED";

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "connections", label: "Connections" },
    { id: "payments", label: "Payments" },
    { id: "activity", label: "Activity" },
    { id: "admin", label: "Admin History" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between sticky top-0 z-20 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="text-slate-400 hover:text-slate-600 w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
          </button>
          <div>
            <h1 className="font-bold text-slate-800">{seeker?.fullName || "Unknown"}</h1>
            <p className="text-xs text-slate-400">{user?.phoneNumber} · Joined {fmtDate(seeker?.createdAt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${STATUS_COLORS[user?.accountStatus] ?? "bg-slate-100 text-slate-600"}`}>
            {user?.accountStatus}
          </span>
          <span className={`text-xs font-semibold px-3 py-1 rounded-full ${PACK_COLORS[pack?.packType] ?? "bg-slate-100 text-slate-600"}`}>
            {pack?.packType ?? "free"}
          </span>
          {isBlocked ? (
            <button
              onClick={() => handleQuickAction("unblock")}
              disabled={acting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={acting ? faSpinner : faCheckCircle} spin={acting} />
              Unblock
            </button>
          ) : (
            <button
              onClick={() => handleQuickAction("block")}
              disabled={acting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={acting ? faSpinner : faBan} spin={acting} />
              Block
            </button>
          )}
          <button
            onClick={() => setNotifyModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <FontAwesomeIcon icon={faBell} />
            Notify
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-100 px-6 flex gap-1">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === t.id
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* ── OVERVIEW TAB ────────────────────────────────────────────── */}
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Left column — Identity + Pack */}
            <div className="lg:col-span-1 space-y-4">
              {/* Identity card */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <SectionTitle icon={faIdBadge} title="Identity" />
                <div className="space-y-2.5">
                  {[
                    { label: "Full Name", value: seeker?.fullName },
                    { label: "Email", value: seeker?.emailId || "—" },
                    { label: "Phone", value: user?.phoneNumber },
                    { label: "Religion", value: seeker?.religion || "—" },
                    { label: "Caste", value: seeker?.caste || "—" },
                    { label: "Sub Caste", value: seeker?.subCaste || "—" },
                    { label: "Member Since", value: fmtDate(user?.memberSince) },
                    { label: "Last Seen", value: user?.lastSeen ? fmtDateTime(user.lastSeen) : "No activity yet" },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between items-start gap-3">
                      <span className="text-xs text-slate-400 shrink-0">{label}</span>
                      <span className="text-xs font-medium text-slate-700 text-right">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pack Quotas */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <SectionTitle icon={faBoxOpen} title="Pack & Quotas"
                  sub={pack?.isExpired ? "⚠ Expired" : pack?.packExpiry ? `Expires ${fmtDate(pack.packExpiry)}` : ""} />
                {pack ? (
                  <div className="space-y-4">
                    {Object.values(pack.quotas).map(q => (
                      <QuotaBar key={q.label} label={q.label} used={q.used} limit={q.limit} />
                    ))}
                    <div className="pt-2 border-t border-slate-50 space-y-1.5">
                      {Object.entries(pack.flags).map(([key, val]) => (
                        <div key={key} className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">{key.replace(/([A-Z])/g, ' $1')}</span>
                          <FontAwesomeIcon
                            icon={val ? faCheckCircle : faTimesCircle}
                            className={val ? "text-emerald-500" : "text-slate-300"}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">No pack assigned</p>
                )}
              </div>
            </div>

            {/* Right column — Profiles + Alignment + Quick stats */}
            <div className="lg:col-span-2 space-y-4">
              {/* Quick stats row */}
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: "Connects Sent", value: fmt(connections?.sent?.total), color: "text-indigo-600" },
                  { label: "Received", value: fmt(connections?.received?.total), color: "text-violet-600" },
                  { label: "Accepted", value: fmt(connections?.sent?.accepted), color: "text-emerald-600" },
                  { label: "DNA Complete", value: `${alignment?.pct}%`, color: alignment?.pct >= 70 ? "text-emerald-600" : "text-amber-500" },
                ].map(s => (
                  <div key={s.label} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 text-center">
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* BG Profiles */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <SectionTitle icon={faUser} title="Bride/Groom Profiles"
                  sub={`${profiles?.length ?? 0} profile${profiles?.length !== 1 ? "s" : ""} created`} />
                {profiles?.length === 0 ? (
                  <div className="text-center py-6">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-amber-400 text-xl mb-2" />
                    <p className="text-xs text-slate-400">No profile created yet — user hasn't completed onboarding</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {profiles.map(p => (
                      <div key={p._id}
                        onClick={() => navigate(`/user-management/bride-groom-profile/view/${p._id}`)}
                        title="Open full bride/groom profile"
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl cursor-pointer hover:bg-slate-100 hover:ring-1 hover:ring-indigo-200 transition">
                        <div className="w-10 h-10 rounded-lg bg-slate-200 overflow-hidden shrink-0">
                          {p.profileImage && !p.profileImage.includes("pending_upload") ? (
                            <img src={p.profileImage} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FontAwesomeIcon icon={faUser} className="text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-700">{p.firstName} {p.lastName}</p>
                          <p className="text-xs text-slate-400">{p.gender} · Created {fmtDate(p.createdAt)}</p>
                        </div>
                        <div className="flex gap-1.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            p.isApproved === 'APPROVED' ? "bg-emerald-100 text-emerald-700" :
                            p.isApproved === 'REJECTED' ? "bg-red-100 text-red-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>
                            {p.isApproved ?? "Pending"}
                          </span>
                          {(!p.profileImage || p.profileImage.includes("pending_upload")) ? (
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">
                              📷 no photo
                            </span>
                          ) : (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              p.photoReviewStatus === 'approved' ? "bg-emerald-100 text-emerald-700" :
                              p.photoReviewStatus === 'rejected' ? "bg-red-100 text-red-700" :
                              "bg-slate-100 text-slate-600"
                            }`}>
                              📷 {p.photoReviewStatus ?? "pending"}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Match DNA Completion */}
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <SectionTitle icon={faBrain} title="Match DNA (Alignment)"
                  sub={`${alignment?.answered} of ${alignment?.total} questions answered`} />
                <div className="flex items-center gap-4">
                  <div className="relative w-20 h-20 shrink-0">
                    <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
                      <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F1F5F9" strokeWidth="3" />
                      <circle
                        cx="18" cy="18" r="15.9" fill="none"
                        stroke={alignment?.pct >= 70 ? "#10B981" : alignment?.pct >= 40 ? "#F59E0B" : "#EF4444"}
                        strokeWidth="3"
                        strokeDasharray={`${alignment?.pct} ${100 - (alignment?.pct ?? 0)}`}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-lg font-bold text-slate-700">{alignment?.pct}%</span>
                    </div>
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${
                      alignment?.pct >= 70 ? "text-emerald-600" :
                      alignment?.pct >= 40 ? "text-amber-600" : "text-red-500"
                    }`}>
                      {alignment?.pct >= 70 ? "Well answered" : alignment?.pct >= 40 ? "Partially complete" : "Low completion"}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {alignment?.answered} answered · {(alignment?.total ?? 0) - (alignment?.answered ?? 0)} remaining
                    </p>
                    {alignment?.pct < 40 && (
                      <p className="text-xs text-amber-600 mt-1.5 bg-amber-50 px-2 py-1 rounded-lg">
                        Low DNA completion reduces match quality
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── CONNECTIONS TAB ─────────────────────────────────────────── */}
        {activeTab === "connections" && (
          <div className="space-y-5">
            {/* Summary grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Sent", value: connections?.sent?.total, color: "text-indigo-700", bg: "bg-indigo-50" },
                { label: "Sent Accepted", value: connections?.sent?.accepted, color: "text-emerald-700", bg: "bg-emerald-50" },
                { label: "Received", value: connections?.received?.total, color: "text-violet-700", bg: "bg-violet-50" },
                { label: "Received Accepted", value: connections?.received?.accepted, color: "text-emerald-700", bg: "bg-emerald-50" },
              ].map(s => (
                <div key={s.label} className={`${s.bg} rounded-2xl p-5 text-center`}>
                  <p className={`text-3xl font-bold ${s.color}`}>{fmt(s.value)}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Recent sent + received */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {[
                { title: "Recently Sent", list: connections?.recentSent, dir: "sent" },
                { title: "Recently Received", list: connections?.recentReceived, dir: "received" },
              ].map(({ title, list, dir }) => (
                <div key={dir} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                  <p className="font-semibold text-slate-800 mb-4 text-sm">{title}</p>
                  {!list?.length ? (
                    <p className="text-xs text-slate-400 text-center py-6">No recent {dir} connections</p>
                  ) : (
                    <div className="space-y-2">
                      {list.map((c, i) => {
                        const other = dir === "sent" ? c.receiverUserId : c.senderUserId;
                        return (
                          <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                            <div>
                              <p className="text-sm font-medium text-slate-700">{other?._phoneNumber || "Unknown"}</p>
                              <p className="text-xs text-slate-400">{fmtDate(c.createdAt)}</p>
                            </div>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              c.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                              c.status === "declined" ? "bg-red-100 text-red-700" :
                              c.status === "expired" ? "bg-slate-100 text-slate-500" :
                              "bg-amber-100 text-amber-700"
                            }`}>
                              {c.status}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── PAYMENTS TAB ────────────────────────────────────────────── */}
        {activeTab === "payments" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-50">
              <p className="font-semibold text-slate-800">Payment History</p>
              <p className="text-xs text-slate-400">Last 10 transactions</p>
            </div>
            {!payments?.length ? (
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faCreditCard} className="text-3xl text-slate-200 mb-3" />
                <p className="text-sm text-slate-400">No payments found</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {["Txn ID", "Amount", "Pack", "Purpose", "Status", "Promo", "Date"].map(h => (
                      <th key={h} className="text-left text-xs font-semibold text-slate-400 px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {payments.map((p, i) => (
                    <tr key={i} className="hover:bg-slate-50 transition">
                      <td className="px-4 py-3 text-xs font-mono text-indigo-600">{p.readableTransactionId || "—"}</td>
                      <td className="px-4 py-3 font-semibold text-slate-800">₹{fmt(p.amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${PACK_COLORS[p.packType] ?? "bg-slate-100 text-slate-600"}`}>
                          {p.packType || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{(p.payment_purpose || "").replace(/_/g, " ")}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                          p.payment_status === "SUCCESS" ? "bg-emerald-100 text-emerald-700" :
                          p.payment_status === "FAILED" ? "bg-red-100 text-red-700" :
                          "bg-amber-100 text-amber-700"
                        }`}>
                          {p.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-400">{p.promoCode || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-400">{fmtDate(p.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ── ACTIVITY TAB ─────────────────────────────────────────────── */}
        {activeTab === "activity" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(activity ?? {}).map(([event, { count, avgDuration }]) => (
                <div key={event} className="bg-white rounded-xl border border-slate-100 shadow-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FontAwesomeIcon
                      icon={EVENT_ICONS[event] ?? faCircle}
                      className="text-indigo-400 text-sm"
                    />
                    <p className="text-xs text-slate-400">{EVENT_LABELS[event] ?? event}</p>
                  </div>
                  <p className="text-2xl font-bold text-slate-800">{fmt(count)}</p>
                  {avgDuration && (
                    <p className="text-xs text-slate-400 mt-0.5">avg {avgDuration}s per view</p>
                  )}
                </div>
              ))}
              {!Object.keys(activity ?? {}).length && (
                <div className="col-span-4 text-center py-12 text-slate-400">
                  <FontAwesomeIcon icon={faHandPointRight} className="text-3xl mb-3 text-slate-200" />
                  <p>No activity recorded yet for this user</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── ADMIN HISTORY TAB ────────────────────────────────────────── */}
        {activeTab === "admin" && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-50">
              <p className="font-semibold text-slate-800">Admin Actions on this User</p>
            </div>
            {!adminHistory?.length ? (
              <div className="text-center py-12 text-slate-400">
                <FontAwesomeIcon icon={faShieldHalved} className="text-3xl text-slate-200 mb-3" />
                <p className="text-sm">No admin actions recorded</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-50">
                {adminHistory.map((log, i) => (
                  <div key={i} className="px-5 py-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <FontAwesomeIcon icon={faHistory} className="text-slate-400 text-xs" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-700">{log.action?.replace(/_/g, " ")}</p>
                        <p className="text-xs text-slate-400">{fmtDateTime(log.createdAt)}</p>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">by {log.adminEmail}</p>
                      {log.after && Object.keys(log.after).length > 0 && (
                        <pre className="text-xs bg-slate-50 rounded-lg p-2 mt-2 overflow-x-auto text-slate-600">
                          {JSON.stringify(log.after, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Push Notification Modal */}
      {notifyModal && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setNotifyModal(false)}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faBell} className="text-indigo-600" />
                <h3 className="text-base font-semibold text-slate-800">Send Push Notification</h3>
              </div>
              <button
                onClick={() => setNotifyModal(false)}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <p className="text-xs text-slate-400">To: {user?.phoneNumber}</p>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={notifyForm.title}
                  onChange={(e) => setNotifyForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Notification title"
                  className="w-full text-sm px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Message <span className="text-red-500">*</span></label>
                <textarea
                  value={notifyForm.body}
                  onChange={(e) => setNotifyForm(f => ({ ...f, body: e.target.value }))}
                  placeholder="Message body"
                  rows={3}
                  className="w-full text-sm px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
              <button
                onClick={() => { setNotifyModal(false); setNotifyForm({ title: '', body: '' }); }}
                className="flex-1 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!notifyForm.title.trim() || !notifyForm.body.trim()) {
                    toast.error("Title and message are required");
                    return;
                  }
                  setNotifying(true);
                  try {
                    const res = await func.post(`mst-admin/match-seekers/${seekerId}/notify`, {
                      title: notifyForm.title,
                      body: notifyForm.body,
                    });
                    if (res?.status === 200) {
                      toast.success("Notification sent");
                      setNotifyModal(false);
                      setNotifyForm({ title: '', body: '' });
                    } else {
                      toast.error("Failed to send notification");
                    }
                  } catch {
                    toast.error("Failed to send notification");
                  }
                  setNotifying(false);
                }}
                disabled={notifying}
                className="flex-1 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {notifying && <FontAwesomeIcon icon={faSpinner} spin className="text-xs" />}
                Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDeepDive;
